import type { GetWebcastStreamQuery, GetWebcastViewerQuery } from '../../../../../generated/graphql-viewer';

import { classNames } from '@movingimage-evp/mi-ui-component-library';
import { useState } from 'react';
import { useParams } from 'react-router';
import { findEventTitle } from 'src/utils/graphql-helpers';

import viewerPlaceholderImage from '../../../../../assets/viewer-placeholder.svg';
import { State } from '../../../../../generated/graphql-viewer';
import { Content } from '../../../../components/content';
import { useSelectedLanguage } from '../../../../providers/selected-language-provider';
import styles from '../../stream.module.css';
import { CoverVideoPlayer } from '../cover-video-player';
import { ViewerHeader } from '../header/header';

type Props = {
  webcast: GetWebcastStreamQuery['webcast'] & GetWebcastViewerQuery['webcast'];
};

export function PreAndPostLiveView({ webcast }: Props) {
  const { webcastId = '' } = useParams();
  const { selectedLanguage, primaryLanguage, isCustomLanguage } = useSelectedLanguage();
  const [playerOverlayHidden, setPlayerOverlayHidden] = useState(false);

  const webcastState = webcast?.state;
  const accountId = webcast?.accountId;
  const lsproId = webcast?.lsproId;
  const primaryColor = webcast?.appearance?.primaryColor;
  const iconColor = webcast?.appearance?.iconColor;

  const logoUrl = webcast?.appearance?.logoUrl || undefined;
  const horizontalLogoUrl = webcast?.appearance?.horizontalLogoUrl || undefined;
  const language = isCustomLanguage(selectedLanguage?.code) ? primaryLanguage?.code : selectedLanguage?.code;
  const content = webcast?.contents.find((content) => content.language === language) || webcast?.contents[0];
  const title = findEventTitle(webcast?.contents, language, webcast?.primaryLang.code);

  const inIframe = window.location !== window.parent.location;

  const coverImageUrl =
    webcastState && webcast
      ? {
          [State.PRELIVE]: content?.preLive.coverImageUrl,
          [State.LIVE]: null,
          [State.LIVE_TEST]: null,
          [State.POSTLIVE]: content?.postLive.coverImageUrl,
        }[webcastState]
      : undefined;

  const coverVideo =
    webcastState && webcast
      ? {
          [State.PRELIVE]: content?.preLive.video,
          [State.LIVE]: null,
          [State.LIVE_TEST]: null,
          [State.POSTLIVE]: content?.postLive.video,
        }[webcastState]
      : undefined;

  const coverVideoVisible = Boolean(coverVideo);
  const coverImageVisible = !coverVideoVisible;

  return (
    <div data-testid="pre-post-live-view" className={classNames(styles.wrapper, inIframe && styles.inIframe)}>
      <main id="viewer-main" data-testid="viewer-main" className={styles.main}>
        <ViewerHeader
          logoUrl={horizontalLogoUrl || logoUrl}
          languageSwitchHidden={coverVideoVisible && playerOverlayHidden}
          inIframe={inIframe}
        />

        <section>
          <div data-testid="video-wrapper" className={styles.videoWrapper}>
            {coverVideoVisible && coverVideo && (
              <CoverVideoPlayer
                className={styles.player}
                webcastId={webcastId}
                channelId={coverVideo.channelId}
                videoId={coverVideo.videoId}
                accountId={accountId}
                lsproId={lsproId}
                title={title}
                logoUrl={logoUrl || horizontalLogoUrl}
                primaryColor={primaryColor}
                iconColor={iconColor}
                looping
                state={webcastState}
                onOverlayVisibilityChange={setPlayerOverlayHidden}
              />
            )}

            {coverImageVisible && (
              <img
                data-testid="viewer-cover-image"
                className={styles.coverImage}
                src={coverImageUrl || viewerPlaceholderImage}
                alt=""
              />
            )}
          </div>

          {!inIframe && <Content className={styles.content} webcast={webcast} language={language} />}
        </section>
      </main>
    </div>
  );
}
